import * as React from "react";
import { useState, createContext, useContext, useCallback, useEffect } from "react";
import { fetchFrames, fetchMediums, getSignedUrl } from "../../utils/artwork-utils";
import { CURRENT_STATE_PHYSICAL_WORK, DIGTALIZATION_TECHNIQUE } from "../constants/artworks";
import { useForm } from "react-hook-form";
import { processThumbnail } from "../../utils/thumbnails";
import configs from "../../utils/configs";
import { extractUUID, getMediaType } from "../../utils/custom-utils";
import { uploadXmlHTTP, METAVERSE_FOLDER } from "../../utils/media-utils";
import { UPLOAD_DRAWER_TOGGLER_ID } from "./ToolbarContext";
import { useObjectSettings } from "./ObjectSettingsProvider";
import { findAncestorWithComponent } from "../../utils/scene-graph";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { useObjectList } from "../room/useObjectList";
import { isThreeMeshUIValidInput, isYearValid } from "../../utils/three-mesh-ui-utils";
import { getAuthHeaders } from "../../utils/getToken";
import { mapAssetToFormFields } from "../../utils/asset-form-utils";
import { useWorkspaceContext } from "./WorkspaceContext";
import { uploadClaimFields } from "../../utils/upload-claim";
import { normalizeWorkspaceAssetUpdate, persistWorkspaceAsset } from "../../utils/workspace-asset-registration";

const UploadFormContext = createContext();

// Create a custom hook to use the UploadFormContext
export const useUploadFormContext = () => useContext(UploadFormContext);

export const artworkFields = {
  url: "url",
  file: "file",
  href: "href",
  title: "title",
  hideHref: "hideHref",
  artName: "artName",
  description: "description",
  medium: "medium",
  dimensions: "dimensions",
  dimensionsY: "dimensionsY",
  dimensionsX: "dimensionsX",
  dimensionsZ: "dimensionsZ",
  artistName: "artistName",
  artistStatement: "artistStatement",
  artistBio: "artistBio",
  techniqueUsed: "techniqueUsed",
  currentPhysicalWork: "currentPhysicalWork",
  AIcreation: "workCreatedByAI",
  terms: "terms",
  poligons: "poligons",
  fileWeight: "fileWeight",
  enableARTrigger: "enableARTrigger",
  aiToolUsed: "aiToolUsed",
  unit: "unit",
  myMediaId: "myMediaId",
  declareRights: "declareRights",
  artworkDate: "artworkDate",
  authorDate: "authorDate",
  type: "type",
  frame: "frame",
  customMedium: "customMedium",
  isPublicDomainArt: "isPublicDomainArt"
};

export const ARTWORK_TYPES = {
  DIGITAL_ART: "Digital Art",
  DIGITAL_TWIN_ART: "Digital Twin Art"
};

export const ASSET_CATEGORIES = {
  DIGITAL_ART: "digital-art",
  DIGITAL_TWIN_ART: "digital-twin-art"
};

// artworkUpdateUrl will be built dynamically based on selected workspace

const parseDimensionInches = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = String(value).trim();
  if (trimmed === "") return null;

  const inchesPattern = /^([\d.]+)\s*(?:in|inch|inches|")?$/i;
  const cmPattern = /^([\d.]+)\s*cm$/i;

  if (inchesPattern.test(trimmed)) {
    const match = trimmed.match(inchesPattern);
    const num = Number(match[1]);
    return Number.isFinite(num) ? num : null;
  }

  if (cmPattern.test(trimmed)) {
    const match = trimmed.match(cmPattern);
    const num = Number(match[1]);
    if (Number.isFinite(num)) {
      return +(num / 2.54);
    }
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildDimensionsText = (widthInches, heightInches, depthInches) => {
  const format = value => {
    if (!Number.isFinite(value)) return null;
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2).replace(/\.?0+$/, "");
  };

  const parts = [];
  const w = format(widthInches);
  const h = format(heightInches);
  const d = format(depthInches);

  if (w) parts.push(w);
  if (h) parts.push(h);
  if (d) parts.push(d);

  return parts.length > 0 ? `${parts.join(" x ")} in` : "";
};

// Helper function to build assets URL with workspace (for API calls)
const buildAssetsUrl = (selectedWorkspace, path = "") => {
  const baseUrl = configs["PERSISTENCE_API_URL"];
  if (selectedWorkspace?.id) {
    return `${baseUrl}/workspaces/${selectedWorkspace.id}/assets${path}`;
  }
  return `${baseUrl}/assets${path}`;
};

// Helper function to build assets URL without workspace (for scene spawning)
const buildAssetsUrlForScene = (path = "") => {
  const baseUrl = configs["PERSISTENCE_API_URL"];
  return `${baseUrl}/assets${path}`;
};

export function useUploadForm(scene) {
  const { selectedWorkspace } = useWorkspaceContext();
  const [isDigitalArt, setIsDigitalArt] = useState(false);
  const [isDigitalTwinArt, setIsDigitalTwinArt] = useState(false);
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (isDigitalArt) {
      setValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_ART);
    } else if (isDigitalTwinArt) {
      setValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_TWIN_ART);
    }
  }, [isDigitalArt, isDigitalTwinArt]);

  const { selectedObject } = useObjectSettings();
  const { deselectObject } = useObjectList();

  const { handleSubmit, register, watch, setValue } = useForm();
  const [thumbnail, setThumbnail] = useState("");
  const [indefiniteLoading, setIndefiniteLoading] = useState(false);
  const [mediums, setMedium] = useState([]);
  const [frames, setFrame] = useState([]);
  const [sizes, setSizes] = useState(null);
  const [myMediaAsset, setAssetMyMedia] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [errorUploadUrl, setErrorUploadUrl] = useState(null);
  const [errorUpload, setErrorUpload] = React.useState(false);
  const [disabledSubmit, setDisabledSubmit] = React.useState(true);
  const [hasInvalidFields, setHasInvalidFields] = React.useState(false);
  const [showOptimizeDialog, setShowOptimizeDialog] = React.useState(false);
  const [assetToOptimize, setAssetToOptimize] = React.useState(null);
  const [pendingPlacementData, setPendingPlacementData] = React.useState(null);
  const file = watch(artworkFields.file);
  const url = watch(artworkFields.url);

  const hasFile = file && file.length > 0;

  const hasFileOrURL = hasFile || url;

  // digitalTwin art required fields
  const artTitle = watch(artworkFields.artName);
  const medium = watch(artworkFields.medium);
  const artistName = watch(artworkFields.artistName);
  const terms = watch(artworkFields.terms);
  const declareRights = watch(artworkFields.declareRights);
  const aiToolUsed = watch(artworkFields.aiToolUsed);
  const bio = watch(artworkFields.artistBio);
  const statement = watch(artworkFields.artistStatement);
  const title = watch(artworkFields.title);
  const description = watch(artworkFields.description);
  const artDate = watch(artworkFields.artworkDate);

  useEffect(() => {
    const fieldsToValidate = [title, aiToolUsed, bio, statement, artTitle, description];
    let fieldInvalid = false;
    fieldsToValidate.forEach(field => {
      if (field && !isThreeMeshUIValidInput(field)) {
        fieldInvalid = true;
      }
    });

    if (!isYearValid(artDate)) {
      fieldInvalid = true;
    }

    setHasInvalidFields(fieldInvalid);
  }, [title, aiToolUsed, bio, statement, artTitle, description, artDate]);

  // In edit mode, we don't require file/url since the asset already exists
  const digitalTwinEnable = [
    artTitle,
    artistName,
    terms,
    isEditMode || hasFileOrURL,
    declareRights,
    artDate,
    !loadingUpload
  ].every(item => Boolean(item));

  // digital Art required fields
  const digitalArtEnable = [
    artTitle,
    artistName,
    terms,
    isEditMode || hasFileOrURL,
    declareRights,
    artDate,
    !loadingUpload
  ].every(item => Boolean(item));

  const handleOpenMediaModal = useCallback(
    e => {
      // Entry is = e.detail, entry has url or file, we need to set url or file
      if (e.detail.url) {
        // We need to update the artworkfields with the url
        setValue(artworkFields.url, e.detail.url);
        setValue(artworkFields.file, null);
        window.dispatchEvent(new CustomEvent(`open-${UPLOAD_DRAWER_TOGGLER_ID}`));
      }

      if (e.detail.file) {
        // We need to update the artworkfields with the file
        setValue(artworkFields.url, null);
        setValue(artworkFields.file, e.detail.file);
        window.dispatchEvent(new CustomEvent(`open-${UPLOAD_DRAWER_TOGGLER_ID}`));
      }
    },
    [setValue]
  );

  // Add event listener that hears "open-media-modal" event
  useEffect(() => {
    window.addEventListener("open-media-modal", handleOpenMediaModal);
    return () => {
      window.removeEventListener("open-media-modal", handleOpenMediaModal);
    };
  }, [handleOpenMediaModal]);

  useEffect(() => {
    const updateDisabledStatus = () => {
      if (!isDigitalTwinArt && !isDigitalArt) {
        // For regular file upload: in edit mode only require title, in upload mode require file/url and title
        if (isEditMode) {
          setDisabledSubmit(!title);
        } else {
          setDisabledSubmit(!hasFileOrURL || !title);
        }
      } else if (isDigitalTwinArt) {
        setDisabledSubmit(!digitalTwinEnable || hasInvalidFields);
      } else {
        setDisabledSubmit(!digitalArtEnable || hasInvalidFields);
      }
    };

    updateDisabledStatus();
  }, [
    hasInvalidFields,
    title,
    selectedObject,
    artDate,
    file,
    url,
    isDigitalTwinArt,
    isDigitalArt,
    artTitle,
    medium,
    artistName,
    terms,
    declareRights,
    hasFileOrURL,
    digitalTwinEnable,
    digitalArtEnable,
    loadingUpload,
    isEditMode
  ]);

  const onErrorUrlUploadChange = useCallback(error => {
    setErrorUploadUrl(error);
  }, []);

  const initEditMode = useCallback(asset => {
    setAssetToEdit(asset);
    setIsEditMode(true);
  }, []);

  const populateFormFields = useCallback(
    asset => {
      const mappedFields = mapAssetToFormFields(asset);
      Object.entries(mappedFields).forEach(([field, value]) => {
        setValue(field, value);
      });
    },
    [setValue]
  );

  const onClose = useCallback(() => {
    setIndefiniteLoading(false);
    onErrorUrlUploadChange("");
    // use window event listener
    window.dispatchEvent(new CustomEvent(`close-${UPLOAD_DRAWER_TOGGLER_ID}`));
    onClear();
  }, [onErrorUrlUploadChange]);

  const digitalizationSelect = [
    {
      label: "--",
      value: "--"
    }
  ].concat(
    DIGTALIZATION_TECHNIQUE.map(technique => ({
      value: technique,
      label: technique
    }))
  );

  const currentStateSelect = [
    {
      label: "--",
      value: "--"
    }
  ].concat(
    CURRENT_STATE_PHYSICAL_WORK.map(work => ({
      value: work,
      label: work
    }))
  );

  React.useEffect(() => {
    Object.values(artworkFields).forEach(field => register(field));
  }, [register]);

  const setMediums = useCallback(async () => {
    try {
      let data = await fetchMediums();
      if (data && data.length > 0) {
        data = [
          { name: "--", model: null },
          { name: "Digital File", model: "Digital File" },
          { name: "Custom", model: "Custom" },
          ...data
        ];
      } else {
        data = [
          { name: "--", model: null },
          { name: "Digital File", model: "Digital File" },
          { name: "Custom", model: "Custom" }
        ];
      }
      setMedium(data);
    } catch (error) {
      console.error("Failed to fetch mediums:", error);
      setMedium([
        { name: "--", model: null },
        { name: "Digital File", model: "Digital File" },
        { name: "Custom", model: "Custom" }
      ]);
    }
  }, []);

  const setFrames = useCallback(async () => {
    try {
      let data = await fetchFrames();
      if (data && data.length > 0) {
        data = [{ name: "--", model: null }, ...data];
      } else {
        data = [{ name: "--", model: null }];
      }
      setFrame(data);
    } catch (error) {
      console.error("Failed to fetch mediums:", error);
      setFrame([{ name: "--", model: null }]);
    }
  }, []);

  const getSizes = async () => {
    let x, y, z;
    if (artwork.el.components["media-image"]) {
      const scale = artwork.el.components["media-image"]?.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = xI;
      y = yI;
      z = zI;
    } else {
      const scale = artwork.el.object3DMap.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = (xI * 1.5).toFixed(2);
      y = (yI * 1.5).toFixed(2);
      z = (zI * 1.5).toFixed(2);
    }

    setSizes({ x, y, z });
  };

  const onThumbnailChange = useCallback(thumbnail => setThumbnail(thumbnail), []);

  const processThumbnailForm = useCallback(
    async (entry, fileUrl) => {
      if (getMediaType(fileUrl) == "audio" || getMediaType(fileUrl) == "video") {
        setThumbnail(fileUrl);
      } else {
        const blob = await processThumbnail(entry, fileUrl, { returnBlob: true });

        let urlFile = URL.createObjectURL(blob);
        setThumbnail(urlFile);

        const thumbnailFile = new File([blob], `${entry?.name.split(".").shift()}-thumb.png`, { type: "image/png" });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(thumbnailFile);
        setValue("thumbnail", dataTransfer.files);
      }
    },
    [setValue]
  );

  const onSubmitArtworkProperties = useCallback(
    async data => {
      let {
        [artworkFields.title]: title,
        [artworkFields.url]: url,
        [artworkFields.href]: href,
        [artworkFields.hideHref]: hideHref,
        [artworkFields.artistName]: artistName,
        [artworkFields.artistStatement]: artistStatement,
        [artworkFields.description]: description,
        [artworkFields.dimensionsX]: dimensionsX,
        [artworkFields.dimensionsY]: dimensionsY,
        [artworkFields.dimensionsZ]: dimensionsZ,
        [artworkFields.artName]: artName,
        [artworkFields.unit]: unit,
        [artworkFields.medium]: medium,
        [artworkFields.myMediaId]: myMediaId,
        [artworkFields.enableARTrigger]: enableARTrigger,
        [artworkFields.frame]: frame,
        [artworkFields.customMedium]: customMedium,
        [artworkFields.isPublicDomainArt]: isPublicDomainArt,
        [artworkFields.artistBio]: artistBio,
        [artworkFields.artworkDate]: artworkDate,
        [artworkFields.declareRights]: declareRights,
        [artworkFields.terms]: terms,
        [artworkFields.AIcreation]: AIcreation,
        [artworkFields.aiToolUsed]: aiToolUsed,
        [artworkFields.techniqueUsed]: techniqueUsed,
        [artworkFields.currentPhysicalWork]: currentPhysicalWork,
        [artworkFields.authorDate]: authorDate
      } = data;

      setLoadingUpload(true);

      try {
        let fileUrl = selectedObject.el.components["media-loader"]?.data?.src;
        const ancestorEl = findAncestorWithComponent(selectedObject.el, "media-loader");

        // Determine asset_category
        let assetCategory = "file";
        if (isDigitalArt) {
          assetCategory = ASSET_CATEGORIES.DIGITAL_ART;
        } else if (isDigitalTwinArt) {
          assetCategory = ASSET_CATEGORIES.DIGITAL_TWIN_ART;
        }

        const normalizedWidth = parseDimensionInches(dimensionsX);
        const normalizedHeight = parseDimensionInches(dimensionsY);
        const normalizedDepth = parseDimensionInches(dimensionsZ);
        const normalizedDimensionsText = buildDimensionsText(normalizedWidth, normalizedHeight, normalizedDepth);

        // Build artwork metadata structure (same as FluentFileUpload)
        const artworkMetadata = {
          artistBio: artistBio || "",
          declareRights: declareRights || false,
          terms: terms || false,
          workCreatedByAI: AIcreation || false,
          aiToolUsed: aiToolUsed || "",
          customMedium: customMedium || "",
          authorDate: authorDate || "",
          type:
            assetCategory === ASSET_CATEGORIES.DIGITAL_TWIN_ART
              ? ARTWORK_TYPES.DIGITAL_TWIN_ART
              : ARTWORK_TYPES.DIGITAL_ART,
          asset_category: assetCategory
        };

        // Add category-specific fields
        if (assetCategory === ASSET_CATEGORIES.DIGITAL_TWIN_ART) {
          artworkMetadata.techniqueUsed = techniqueUsed || "";
          artworkMetadata.currentPhysicalWork = currentPhysicalWork || "";
          if (medium && typeof medium === "object") {
            artworkMetadata.medium = medium;
          }
          if (frame && typeof frame === "object") {
            artworkMetadata.frame = frame;
          }
        } else if (assetCategory === ASSET_CATEGORIES.DIGITAL_ART) {
          // Digital art can have medium and frame too
          if (medium && typeof medium === "object") {
            artworkMetadata.medium = medium;
          }
          if (frame && typeof frame === "object") {
            artworkMetadata.frame = frame;
          }
        }

        const metadataToSave = {
          artwork: {
            title: artName || title,
            author: artistName,
            description: description || "",
            artworkDate: artworkDate || "",
            medium: medium?.label || medium?.name || "",
            dimensions: normalizedDimensionsText,
            dimensionsX: normalizedWidth || "",
            dimensionsY: normalizedHeight || "",
            dimensionsZ: normalizedDepth || "",
            artistStatement: artistStatement || "",
            asset_category: assetCategory,
            metadata: artworkMetadata
          }
        };

        // PATCH the asset with metadata
        const body = {
          title: artName || title,
          description,
          enable_ar_trigger: !!enableARTrigger,
          metadata: metadataToSave,
          asset_category: assetCategory
        };

        const artworkUpdateUrl = buildAssetsUrl(selectedWorkspace);
        const artworkPatchUrl = `${artworkUpdateUrl}/${myMediaId}`;
        const patchResponse = await fetch(artworkPatchUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(await getAuthHeaders(artworkPatchUrl, "PATCH"))
          },
          body: JSON.stringify(normalizeWorkspaceAssetUpdate(body))
        });

        const updatedAsset = await patchResponse.json();
        const assetId = updatedAsset.data?.id || updatedAsset.id || myMediaId;
        const assetUuid = updatedAsset.data?.uuid || updatedAsset.uuid;

        // Trigger scene update via event (same as FluentFileUpload edit flow)
        if (typeof AFRAME !== "undefined" && AFRAME.scenes[0]) {
          AFRAME.scenes[0].emit("asset-metadata-updated", {
            assetId: assetId,
            assetUuid: assetUuid,
            updatedMetadata: {
              title: artName || title,
              description,
              asset_category: assetCategory,
              metadata: metadataToSave
            }
          });
        }

        setTimeout(() => {
          setLoadingUpload(false);
          deselectObject();
          window.dispatchEvent(new CustomEvent("close-object-settings"));
        }, 1000); // Reduced from 4000ms
      } catch (error) {
        console.error("Failed to update artwork properties:", error);
        setLoadingUpload(false);
        notificationsCenter.error("Failed to update artwork, please try again");
      }
    },
    [deselectObject, frames, mediums, selectedObject, isDigitalArt, isDigitalTwinArt]
  );

  const onSubmitForm = useCallback(
    async (data, options = {}) => {
      const { skipScenePlacement = false, workspaceOverride = null } = options || {};
      const targetWorkspace = workspaceOverride || selectedWorkspace;
      const {
        [artworkFields.title]: title,
        [artworkFields.file]: file,
        [artworkFields.url]: url,
        [artworkFields.href]: href,
        [artworkFields.hideHref]: hideHref,
        [artworkFields.artistName]: artistName,
        [artworkFields.artistStatement]: artistStatement,
        [artworkFields.description]: description,
        [artworkFields.dimensionsX]: dimensionsX,
        [artworkFields.dimensionsY]: dimensionsY,
        [artworkFields.dimensionsZ]: dimensionsZ,
        [artworkFields.artName]: artName,
        [artworkFields.unit]: unit,
        [artworkFields.medium]: medium,
        [artworkFields.myMediaId]: myMediaId,
        [artworkFields.enableARTrigger]: enableARTrigger,
        [artworkFields.frame]: frame,
        [artworkFields.artistBio]: artistBio,
        [artworkFields.artworkDate]: artworkDate,
        [artworkFields.declareRights]: declareRights,
        [artworkFields.terms]: terms,
        [artworkFields.AIcreation]: AIcreation,
        [artworkFields.aiToolUsed]: aiToolUsed,
        [artworkFields.techniqueUsed]: techniqueUsed,
        [artworkFields.currentPhysicalWork]: currentPhysicalWork,
        [artworkFields.customMedium]: customMedium,
        [artworkFields.authorDate]: authorDate
      } = data;
      setLoadingUpload(true);

      try {
        let fileUrl;
        let originalFileType = null;
        let activeMyMediaId = myMediaId;
        let pendingAssetEntry = null;

        // NEW: Handle File Object Upload
        if (file && file.length && file[0].fileObject) {
          const acceptedFile = file[0].fileObject;
          const thumbnailBlob = file[0].thumbnailBlob;

          // 1. Upload File
          const fileResponse = await uploadXmlHTTP(acceptedFile, acceptedFile.type, undefined, targetWorkspace?.id);
          fileUrl = fileResponse.download_url;

          // 2. Upload Thumbnail (if exists)
          let thumbnailUrl = null;
          let thumbnailSigned = null;
          if (thumbnailBlob && !acceptedFile.type.includes("audio/")) {
            try {
              const thumbnailFile = new File([thumbnailBlob], `${acceptedFile.name}-thumb.png`, { type: "image/png" });
              thumbnailSigned = await getSignedUrl({
                file: thumbnailFile,
                folder: METAVERSE_FOLDER,
                workspaceId: targetWorkspace?.id
              });

              await fetch(thumbnailSigned.presigned_url, {
                method: "PUT",
                headers: {
                  "Content-Type": "image/png",
                  "x-ms-blob-type": "BlockBlob"
                },
                body: thumbnailFile
              });
              thumbnailUrl = thumbnailSigned.download_url;
            } catch (err) {
              console.error("Error uploading thumbnail", err);
            }
          }

          // 3. Create Asset (POST)
          const fileNameParts = acceptedFile.name.split(".");
          const extension = fileNameParts.pop()?.toLowerCase();
          const name = fileNameParts.join(".");
          const fileName = `${name}.${extension}`;

          if (["glb", "gltf", "obj", "fbx", "dae", "3ds"].includes(extension)) {
            originalFileType = "model";
          }

          pendingAssetEntry = {
            name: fileName,
            title: title || fileName,
            path: fileResponse.key,
            type: getMediaType(extension),
            asset_type: "my-media",
            size: acceptedFile.size,
            source: "metaverse",
            thumbnail: thumbnailUrl,
            ...uploadClaimFields(fileResponse, thumbnailSigned)
          };
        } else if (file && file.length) {
          // Existing flow: URL upload or already handled
          const acceptedFile = file[0];
          fileUrl = acceptedFile.downloableUrl;
          // Detect original file type before persistence
          const fileName = acceptedFile.name || acceptedFile.filename || "";
          const extension = fileName.split(".").pop()?.toLowerCase();
          if (["glb", "gltf", "obj", "fbx", "dae", "3ds"].includes(extension)) {
            originalFileType = "model";
          }
        } else if (url) {
          // Detect model type from URL
          const urlExtension = url.split(".").pop()?.toLowerCase();
          if (["glb", "gltf", "obj", "fbx", "dae", "3ds"].includes(urlExtension)) {
            originalFileType = "model";
          }
        }

        const finalUrl = fileUrl || url;
        // Build metadata based on whether it's an artwork or regular file
        let metadataToSave = {};
        let assetCategory = "file";

        if (artistName) {
          // It's an artwork - build artwork metadata

          // Determine asset_category
          if (isDigitalArt) {
            assetCategory = ASSET_CATEGORIES.DIGITAL_ART;
          } else if (isDigitalTwinArt) {
            assetCategory = ASSET_CATEGORIES.DIGITAL_TWIN_ART;
          }

          const normalizedWidth = parseDimensionInches(dimensionsX);
          const normalizedHeight = parseDimensionInches(dimensionsY);
          const normalizedDepth = parseDimensionInches(dimensionsZ);
          const normalizedDimensionsText = buildDimensionsText(normalizedWidth, normalizedHeight, normalizedDepth);

          // Build artwork metadata structure
          const artworkMetadata = {
            artistBio: artistBio || "",
            declareRights: declareRights || false,
            terms: terms || false,
            workCreatedByAI: AIcreation || false,
            aiToolUsed: aiToolUsed || "",
            customMedium: customMedium || "",
            authorDate: authorDate || "",
            type:
              assetCategory === ASSET_CATEGORIES.DIGITAL_TWIN_ART
                ? ARTWORK_TYPES.DIGITAL_TWIN_ART
                : ARTWORK_TYPES.DIGITAL_ART,
            asset_category: assetCategory
          };

          // Add category-specific fields
          if (assetCategory === ASSET_CATEGORIES.DIGITAL_TWIN_ART) {
            artworkMetadata.techniqueUsed = techniqueUsed || "";
            artworkMetadata.currentPhysicalWork = currentPhysicalWork || "";
          }

          // Add medium and frame if they exist
          if (medium && typeof medium === "object") {
            artworkMetadata.medium = medium;
          }
          if (frame && typeof frame === "object") {
            artworkMetadata.frame = frame;
          }

          metadataToSave = {
            artwork: {
              title: artName || title,
              author: artistName,
              description: description || "",
              artworkDate: artworkDate || "",
              medium: medium?.label || medium?.name || "",
              dimensions: normalizedDimensionsText,
              dimensionsX: normalizedWidth || "",
              dimensionsY: normalizedHeight || "",
              dimensionsZ: normalizedDepth || "",
              artistStatement: artistStatement || "",
              asset_category: assetCategory,
              metadata: artworkMetadata
            }
          };
        }

        // New uploads register their final metadata atomically; existing Assets use PATCH.
        const myMediaUrl = buildAssetsUrl(targetWorkspace);
        const patchBody = {
          title: artName || title || pendingAssetEntry?.title,
          description,
          enable_ar_trigger: !!enableARTrigger,
          asset_category: assetCategory
        };

        if (Object.keys(metadataToSave).length > 0) {
          patchBody.metadata = metadataToSave;
        }
        const persisted = await persistWorkspaceAsset({
          assetsUrl: myMediaUrl,
          newAssetEntry: pendingAssetEntry,
          assetId: activeMyMediaId,
          mutation: patchBody,
          getHeaders: getAuthHeaders
        });
        activeMyMediaId = persisted.assetId;
        const updatedAsset = persisted.response;
        const assetUuid = updatedAsset.data?.uuid || updatedAsset.uuid || extractUUID(finalUrl);

        // Spawn using /assets/{uuid} URL - same as sidebar assets (without workspace prefix)
        const assetUrl = buildAssetsUrlForScene(`/${assetUuid}`);
        const mediaType = getMediaType(finalUrl || assetUrl);

        const placementPayload = {
          src: assetUrl,
          href,
          hideHref,
          smoothTransition: false,
          fileIsOwned: true,
          assetSrc: assetUrl
        };

        const placeInScene = (overrideSrc, extraPayload = {}) => {
          const payload = {
            ...placementPayload,
            ...(overrideSrc ? { src: overrideSrc } : {}),
            ...(extraPayload || {})
          };
          if (!payload.assetSrc) {
            payload.assetSrc = placementPayload.assetSrc;
          }
          scene.emit("add_media", payload);
        };

        const persistedAsset = updatedAsset?.data || updatedAsset || null;

        // Check if we should show optimization dialog for 3D models
        const shouldShowOptimizeDialog = originalFileType === "model" && !skipScenePlacement;

        if (shouldShowOptimizeDialog) {
          // Prepare asset data for optimization dialog
          const optimizeAsset = {
            id: persistedAsset?.id || activeMyMediaId,
            uuid: assetUuid,
            title: artName || title,
            url: finalUrl, // Use finalUrl for model analysis, not assetUrl
            size: persistedAsset?.size || 0,
            content_type: persistedAsset?.content_type || "model/gltf-binary"
          };

          // Store placement data for later use
          setPendingPlacementData({
            placementPayload,
            placeInScene,
            persistedAsset
          });

          setAssetToOptimize(optimizeAsset);
          setShowOptimizeDialog(true);

          // Don't close the upload dialog yet
          setLoadingUpload(false);
        } else {
          // Normal flow: place in scene immediately
          if (!skipScenePlacement) {
            placeInScene();
          }

          setLoadingUpload(false);
          onClose();
        }

        return {
          placementPayload,
          placeInScene,
          mediaType,
          assetId: persistedAsset?.id || activeMyMediaId,
          assetUuid,
          persistedAsset,
          assetUrl,
          downloadUrl: finalUrl
        };
      } catch (error) {
        console.error("Failed to upload media:", error);
        setLoadingUpload(false);
        if (error?.status !== 403) {
          notificationsCenter.error("Failed to upload asset, please try again");
        }
        throw error;
      }
    },
    [mediums, frames, onClose, scene, isDigitalArt, isDigitalTwinArt, selectedWorkspace]
  );

  // Handle optimization dialog callbacks
  const onOptimized = useCallback(
    (response, options = {}) => {
      const { isCopy } = options;
      const { placeInScene } = pendingPlacementData || {};

      if (placeInScene && response?.data?.optimizedAsset?.api_url) {
        // Use the optimized asset URL from backend response
        const optimizedUrl = response.data.optimizedAsset.api_url;
        placeInScene(optimizedUrl);
      }

      // Clean up and close dialogs
      setShowOptimizeDialog(false);
      setAssetToOptimize(null);
      setPendingPlacementData(null);
      onClose();
    },
    [pendingPlacementData, onClose]
  );

  const onOptimizeDialogClose = useCallback(() => {
    const { placeInScene } = pendingPlacementData || {};

    // Place original asset in scene when optimization is cancelled/skipped
    if (placeInScene) {
      placeInScene();
    }

    // Clean up and close dialogs
    setShowOptimizeDialog(false);
    setAssetToOptimize(null);
    setPendingPlacementData(null);
    onClose();
  }, [pendingPlacementData, onClose]);

  const onClear = useCallback(() => {
    setAssetToEdit(null);
    setIsEditMode(false);
    setAssetMyMedia(null);
    setThumbnail("");
    setLoadingUpload(false);
    Object.values(artworkFields).forEach(field => setValue(field, ""));
  }, [setValue, setAssetMyMedia, setThumbnail]);

  const onChangeForm = useCallback(
    (e, field, type = "text", value = "") => {
      if (field === "url" && hasFile) {
        return;
      }

      if (type == "value") {
        setValue(field, value);
        return;
      }

      setValue(field, type === "toggle" ? e.target.checked : e.target.value);
    },
    [hasFile, setValue]
  );

  const handleFileLoadedDropzone = useCallback(
    async (fileObject, thumbnailBlob) => {
      setIndefiniteLoading(false);

      // Create blob URL for local preview
      let thumbnailUrl = null;
      if (thumbnailBlob) {
        thumbnailUrl = URL.createObjectURL(thumbnailBlob);
      } else if (fileObject.type.includes("image/")) {
        thumbnailUrl = URL.createObjectURL(fileObject);
      } else if (fileObject.type.includes("video/")) {
        thumbnailUrl = URL.createObjectURL(fileObject);
      }

      const files = [
        {
          fileObject: fileObject,
          name: fileObject.name,
          type: fileObject.type,
          size: fileObject.size,
          thumbnailBlob: thumbnailBlob,
          localThumbnailUrl: thumbnailUrl,
          downloableUrl: null
        }
      ];

      setValue(artworkFields.file, files);

      setThumbnail(thumbnailUrl);

      // Automatically set the default title/artName based on uploaded file name
      let fileName = fileObject.name || "";
      // Remove the extension for a cleaner default title
      if (fileName.includes(".")) {
        fileName = fileName.substring(0, fileName.lastIndexOf("."));
      }

      if (fileName) {
        setValue(artworkFields.title, fileName);
        setValue(artworkFields.artName, fileName);
      }

      onErrorUrlUploadChange("");
    },
    [onErrorUrlUploadChange, setValue]
  );

  return {
    mediums,
    setMediums,
    frames,
    setFrames,
    getSizes,
    digitalizationSelect,
    currentStateSelect,
    thumbnail,
    onThumbnailChange,
    formSetValue: setValue,
    formRegister: register,
    formWatch: watch,
    processThumbnail: processThumbnailForm,
    onSubmitForm,
    handleSubmit,
    onChangeForm,
    handleFileLoadedDropzone,
    onClear,
    onClose,
    disabledSubmit,
    hasFile,
    loadingUpload,
    setLoadingUpload,
    setThumbnail,
    url,
    indefiniteLoading,
    setIndefiniteLoading,
    onErrorUrlUploadChange,
    errorUploadUrl,
    isDigitalArt,
    isDigitalTwinArt,
    setIsDigitalArt,
    setIsDigitalTwinArt,
    onSubmitArtworkProperties,
    medium,
    errorUpload,
    setErrorUpload,
    file,
    assetToEdit,
    isEditMode,
    initEditMode,
    populateFormFields,
    showOptimizeDialog,
    setShowOptimizeDialog,
    assetToOptimize,
    onOptimized,
    onOptimizeDialogClose
  };
}

// Create a provider component
export const UploadFormProvider = ({ children, scene }) => {
  const uploadForm = useUploadForm(scene);

  return <UploadFormContext.Provider value={uploadForm}>{children}</UploadFormContext.Provider>;
};
